import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  PRODUCT_STATE_LABELS,
  PRODUCT_ITEM_STATE_LABELS,
  PRODUCT_KIND_LABELS,
  PRODUCT_PACKAGE_KIND_LABELS,
  PRODUCT_ITEM_KIND_LABELS,
} from '@repo/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  ENROLLMENT_KIND,
  PRODUCT_ITEM_STATE,
  PRODUCT_KIND,
  PRODUCT_PACKAGE_KIND,
  PRODUCT_STATE,
} from '@theailab-dev/parchment';
import type {
  EnrollmentKind,
  ProductItemKind,
  ProductKind,
  ProductPackageKind,
  ProductState,
} from '@theailab-dev/parchment';
import DOMPurify from 'dompurify';
import React, { useState } from 'react';
import { HiPencil, HiTrash, HiPlus, HiExternalLink, HiMenu } from 'react-icons/hi';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  createProductItem,
  updateProductItem,
  deleteProductItem,
  reorderProductItems,
  ProductItemDto,
} from '../../api/productApi';
import { DetailPageLayout } from '../../components/Layout/DetailPageLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { DateRangeFilter } from '../../components/ui/DateRangeFilter';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import ProductItemAddModal from '../../components/ui/ProductItemAddModal';
import ProductItemEditModal from '../../components/ui/ProductItemEditModal';
import { SelectFilter } from '../../components/ui/SelectFilter';
import { useProductDetail } from '../../hooks/useProductDetail';
import { useProductEnrollments } from '../../hooks/useProductEnrollments';
import { formatDateTime } from '../../utils/dateTime';
import { getErrorMessage } from '../../utils/errorHandler';

/**
 * ProductDetail 컴포넌트 - 상품 상세 정보 페이지
 */

/**
 * 이미지 URL을 절대 경로로 변환하는 헬퍼 함수
 * 상대 경로인 경우 API base URL을 붙임
 */
const getImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  // 이미 절대 경로인 경우 그대로 반환
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // 상대 경로인 경우 API base URL을 붙임
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4010';
  // URL이 /로 시작하지 않으면 /를 추가
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_URL}${normalizedUrl}`;
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const productId = id ? parseInt(id, 10) : 0;

  const { data: product, isLoading, error } = useProductDetail(productId);

  // URL 파라미터에서 필터 값 가져오기
  const [searchParams, setSearchParams] = useSearchParams();
  const filterKind = searchParams.get('filterKind') || '';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  const typedFilterKind: EnrollmentKind | undefined = Object.values(ENROLLMENT_KIND).includes(
    filterKind as EnrollmentKind,
  )
    ? (filterKind as EnrollmentKind)
    : undefined;

  // 구매자 목록 페이지네이션 state
  const [enrollmentsPage, setEnrollmentsPage] = useState(1);
  const enrollmentsLimit = 10;

  // 구매자 목록 조회
  const { data: enrollmentsData, isLoading: isLoadingEnrollments } = useProductEnrollments(productId, {
    page: enrollmentsPage,
    limit: enrollmentsLimit,
    filterKind: typedFilterKind,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  // 뒤로 가기 핸들러: location.state에서 이전 경로 정보를 가져오거나, 기본값 사용
  const handleBack = () => {
    const previousPath = (location.state as { from?: string })?.from;
    if (previousPath) {
      // replace: true를 사용하여 히스토리를 교체 (무한 반복 방지)
      // 이렇게 하면 상품 상세가 히스토리에 남지 않으므로,
      // 결제 상세나 주문서 상세에서 navigate(-1)을 사용해도 상품 상세로 돌아가지 않음
      navigate(previousPath, { replace: true });
    } else {
      // location.state가 없으면 기본적으로 목록으로 이동 (쿼리 파라미터 포함)
      const searchParams = new URLSearchParams(location.search);
      const page = searchParams.get('page') || '1';
      navigate(`/dashboard/products?page=${page}`);
    }
  };

  // 삭제 확인 모달 상태
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // 상품 아이템 추가 모달 상태
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // 상품 아이템 수정 모달 상태
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    item: ProductItemDto | null;
  }>({
    isOpen: false,
    item: null,
  });

  // 순서 변경 모드 상태
  const [isReordering, setIsReordering] = useState(false);
  const [localItems, setLocalItems] = useState<ProductItemDto[]>([]);

  // 순서 변경 모드 진입
  const startReordering = () => {
    if (product) {
      setLocalItems([...product.items]);
      setIsReordering(true);
    }
  };

  // 순서 변경 취소
  const cancelReordering = () => {
    setIsReordering(false);
    setLocalItems([]);
  };

  // 드래그 종료 핸들러
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(localItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    if (reorderedItem) {
      items.splice(result.destination.index, 0, reorderedItem);
    }

    setLocalItems(items);
  };

  // 순서 저장 핸들러
  const saveOrder = async () => {
    try {
      const itemIds = localItems.map((item) => item.id);
      await reorderProductItems(productId, itemIds);
      await queryClient.invalidateQueries({ queryKey: ['product', productId] });
      setIsReordering(false);
    } catch (error) {
      console.error('순서 저장 실패:', error);
      alert('순서 저장에 실패했습니다.');
    }
  };

  // 상품 아이템 추가 핸들러
  const handleAddProductItem = async (data: {
    courseId: number | null;
    kind: ProductItemKind;
    originalPrice: number;
    discount: number;
    packageKind?: ProductPackageKind;
  }) => {
    try {
      const requestData = {
        ...data,
        discount: data.discount || 0,
        packageKind: data.packageKind ?? PRODUCT_PACKAGE_KIND.REQUIREMENT,
      };
      await createProductItem(productId, requestData);
      // 상품 상세 정보 다시 불러오기
      await queryClient.invalidateQueries({ queryKey: ['product', productId] });
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('상품 아이템 추가 실패:', error);

      // 백엔드 에러 메시지 추출 (errorHandler 유틸리티 사용)
      const errorMessage = getErrorMessage(error);
      throw new Error(errorMessage);
    }
  };

  // 상품 아이템 수정 핸들러
  const handleUpdateProductItem = async (
    itemId: number,
    data: {
      courseId?: number;
      discount: number;
      packageKind?: ProductPackageKind;
    },
  ) => {
    try {
      const requestData: {
        courseId?: number;
        productItemId?: number;
        discount: number;
        packageKind: ProductPackageKind;
      } = {
        discount: data.discount,
        packageKind: data.packageKind ?? PRODUCT_PACKAGE_KIND.REQUIREMENT,
      };

      // productItemId가 제공된 경우에만 포함 (강의 변경 시)
      if (data.courseId !== undefined) {
        requestData.productItemId = data.courseId; // courseId는 실제로 새로운 productItemId
      }

      console.log('📤 상품 아이템 수정 요청 데이터:', {
        productId,
        itemId,
        requestData,
        discountValue: requestData.discount,
        packageKindValue: requestData.packageKind,
      });

      await updateProductItem(productId, itemId, requestData);

      // 쿼리 캐시 무효화 (현재 페이지에서 useProductDetail이 활성화되어 있으면 자동 refetch됨)
      await queryClient.invalidateQueries({ queryKey: ['product', productId] });

      setEditModal({ isOpen: false, item: null });
    } catch (error) {
      console.error('상품 아이템 수정 실패:', error);
      throw error;
    }
  };

  // 상품 아이템 삭제 핸들러
  const handleDeleteProductItem = async (itemId: number) => {
    try {
      await deleteProductItem(productId, itemId);
      // 상품 상세 정보 다시 불러오기
      await queryClient.invalidateQueries({ queryKey: ['product', productId] });
    } catch (error) {
      console.error('상품 아이템 삭제 실패:', error);
      throw error;
    }
  };

  const getKindBadgeVariant = (kind: ProductKind) => {
    switch (kind) {
      case PRODUCT_KIND.SINGLE:
        return 'success';
      case PRODUCT_KIND.ROADMAP:
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const getStateBadgeVariant = (state: ProductState) => {
    switch (state) {
      case PRODUCT_STATE.NORMAL:
        return 'success';
      case PRODUCT_STATE.HIDDEN:
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const handleFilterKindChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (value) {
      params.set('filterKind', value);
    } else {
      params.delete('filterKind');
    }
    setSearchParams(params);
  };

  const handleDateChange = (type: 'startDate' | 'endDate', value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (value) {
      params.set(type, value);
    } else {
      params.delete(type);
    }
    setSearchParams(params);
  };

  const renderItemRow = (
    item: ProductItemDto,
    index: number,
    isReorderingMode: boolean,
    dragProvided?: any,
    dragSnapshot?: any,
  ) => (
    <tr
      key={item.id}
      ref={dragProvided?.innerRef}
      {...dragProvided?.draggableProps}
      className={`group transition-colors hover:bg-slate-50 ${dragSnapshot?.isDragging ? 'bg-blue-50 shadow-lg' : ''}`}>
      <td className="px-3 py-3 text-xs text-slate-500">
        <div className="flex items-center gap-1">
          {isReorderingMode ? (
            <div
              {...dragProvided?.dragHandleProps}
              className="cursor-grab rounded p-0.5 hover:bg-slate-200 active:cursor-grabbing">
              <HiMenu className="h-3 w-3 text-slate-400" />
            </div>
          ) : (
            <span className="text-center">{item.seq}</span>
          )}
          {isReorderingMode && <span className="text-center text-xs text-slate-300">{index + 1}</span>}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex min-w-0 flex-col">
          <span className="text-xs font-medium text-slate-900">
            {item.course?.title || (
              <span className="text-slate-400">제목 없음 {item.courseId ? `(${item.courseId})` : ''}</span>
            )}
          </span>
          <span className="text-xs text-slate-500">{item.category?.title || item.course?.category?.title || '-'}</span>
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-col items-center gap-0.5">
          <Badge
            variant={item.packageKind === PRODUCT_PACKAGE_KIND.OPTION ? 'neutral' : 'primary'}
            size="sm"
            className="px-1.5 py-0 text-[10px]">
            {PRODUCT_PACKAGE_KIND_LABELS[item.packageKind]}
          </Badge>
          <span className="text-[10px] text-slate-400">{PRODUCT_ITEM_KIND_LABELS[item.kind]}</span>
        </div>
      </td>
      <td className="px-3 py-3">
        <Badge
          variant={item.state === PRODUCT_ITEM_STATE.NORMAL ? 'success' : 'neutral'}
          size="sm"
          className="px-1.5 py-0 text-[10px] whitespace-nowrap">
          {PRODUCT_ITEM_STATE_LABELS[item.state]}
        </Badge>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-xs font-medium whitespace-nowrap text-slate-900">
          {item.originalPrice.toLocaleString()}원
        </span>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-xs whitespace-nowrap text-slate-600">
          {item.discount > 0 ? `${item.discount.toLocaleString()}원` : '-'}
        </span>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-xs font-semibold whitespace-nowrap text-slate-900">
          {(item.originalPrice - item.discount).toLocaleString()}원
        </span>
      </td>
      <td className="px-3 py-3">
        {!isReorderingMode && (
          <div className="flex items-center justify-end gap-1">
            {/* 강의 수정 버튼 (원가 수정 등) */}
            {item.courseId && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/dashboard/courses/${item.courseId}`);
                }}
                title="강의 상세 정보"
                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                <HiExternalLink className="h-4 w-4" />
              </button>
            )}
            {/* 편집 버튼: 항상 표시 */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEditModal({ isOpen: true, item });
              }}
              title="할인가/구성 수정"
              className="rounded p-1.5 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800">
              <HiPencil className="h-4 w-4" />
            </button>
            {/* 삭제 버튼: 로드맵 유형만 표시 */}
            {product?.kind === PRODUCT_KIND.ROADMAP && (
              <button
                type="button"
                disabled={product.items.length <= 1}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirmModal({
                    isOpen: true,
                    title: '상품 아이템 삭제',
                    message: `"${item.course?.title || `아이템 ${item.id}`}" 상품 아이템을 삭제하시겠습니까?`,
                    onConfirm: async () => {
                      try {
                        await handleDeleteProductItem(item.id);
                        setConfirmModal({ ...confirmModal, isOpen: false });
                      } catch (error) {
                        console.error('상품 아이템 삭제 실패:', error);
                        const errorMessage = getErrorMessage(error);
                        alert(errorMessage);
                      }
                    },
                  });
                }}
                title={product.items.length <= 1 ? '최소 1개의 아이템이 필요합니다' : '삭제'}
                className={`rounded p-1.5 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 ${
                  product.items.length <= 1 ? 'text-slate-300' : 'text-red-500 hover:text-red-600'
                }`}>
                <HiTrash className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );

  if (isLoading) {
    return (
      <DetailPageLayout title="상품 상세" onBack={handleBack}>
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DetailPageLayout>
    );
  }

  if (error || !product) {
    return (
      <DetailPageLayout title="상품 상세" onBack={handleBack}>
        <div className="flex h-64 flex-col items-center justify-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">
            {error instanceof Error ? error.message : '상품 정보를 불러오는데 실패했습니다.'}
          </p>
          <Button onClick={handleBack}>목록으로 돌아가기</Button>
        </div>
      </DetailPageLayout>
    );
  }

  const thumbnailUrl = getImageUrl(product.thumbnailUrl);
  const imageUrl = getImageUrl(product.imageUrl);

  return (
    <DetailPageLayout
      title="상품 상세"
      onBack={handleBack}
      actions={
        <Button variant="primary" onClick={() => navigate(`/dashboard/products/${productId}/edit`)}>
          수정
        </Button>
      }>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Left Column: Main Content */}
        <div className="space-y-6 lg:col-span-3">
          {/* 상품 아이템 목록 카드 */}
          <Card
            title={`상품 아이템 (${product.items.length}개)`}
            noPadding
            actions={
              // 로드맵 유형만 추가/순서변경 버튼 표시
              product.kind === PRODUCT_KIND.ROADMAP && (
                <div className="flex gap-2">
                  {isReordering ? (
                    <>
                      <Button type="button" variant="outline" size="sm" onClick={cancelReordering}>
                        취소
                      </Button>
                      <Button type="button" variant="primary" size="sm" onClick={saveOrder}>
                        순서 저장
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={startReordering}
                        disabled={product.items.length <= 1}>
                        순서 변경
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsAddModalOpen(true);
                        }}>
                        <HiPlus className="mr-1 h-4 w-4" />
                        추가
                      </Button>
                    </>
                  )}
                </div>
              )
            }>
            {product.items.length === 0 ? (
              <div className="p-8 text-center">
                <p className="mb-4 text-slate-500">등록된 상품 아이템이 없습니다.</p>
                {/* 로드맵 유형만 추가 버튼 표시 */}
                {product.kind === PRODUCT_KIND.ROADMAP && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsAddModalOpen(true);
                    }}>
                    <HiPlus className="mr-1 h-4 w-4" />
                    상품 아이템 추가
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <DragDropContext onDragEnd={onDragEnd}>
                  <table className="w-full border-collapse text-left">
                    <thead className="border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="w-12 px-3 py-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          순서
                        </th>
                        <th className="min-w-[200px] px-3 py-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          대상 콘텐츠
                        </th>
                        <th className="w-20 px-3 py-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          유형
                        </th>
                        <th className="w-16 px-3 py-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          상태
                        </th>
                        <th className="w-24 px-3 py-2 text-right text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          원가
                        </th>
                        <th className="w-20 px-3 py-2 text-right text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          할인가
                        </th>
                        <th className="w-28 px-3 py-2 text-right text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          최종 가격
                        </th>
                        <th className="w-32 px-3 py-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                          작업
                        </th>
                      </tr>
                    </thead>
                    {isReordering ? (
                      <Droppable droppableId="product-items">
                        {(provided) => (
                          <tbody
                            className="divide-y divide-slate-100 bg-white"
                            {...provided.droppableProps}
                            ref={provided.innerRef}>
                            {localItems.map((item, index) => (
                              <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                                {(provided, snapshot) => renderItemRow(item, index, true, provided, snapshot)}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </tbody>
                        )}
                      </Droppable>
                    ) : (
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {product.items.map((item, index) => renderItemRow(item, index, false))}
                      </tbody>
                    )}
                  </table>
                </DragDropContext>
              </div>
            )}

            {/* 전체 패키지 최종 가격 합계 */}
            {product.items.length > 0 && (
              <div className="border-t border-slate-200 bg-slate-50 px-3 py-3">
                <div className="flex items-center justify-end gap-6">
                  <div className="text-right">
                    <div className="mb-1 text-xs text-slate-500">전체 패키지 최종 가격</div>
                    <div className="text-base font-bold text-slate-900">
                      {product.items
                        .reduce((sum, item) => sum + (item.originalPrice - item.discount), 0)
                        .toLocaleString()}
                      원
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* 구매자 목록 카드 */}
          <Card title={`구매자/부여자 목록 (${enrollmentsData?.meta.total || 0}명)`} noPadding>
            <div className="flex items-center gap-4 px-6 py-1">
              <SelectFilter
                value={filterKind}
                onChange={handleFilterKindChange}
                options={[
                  { value: ENROLLMENT_KIND.PURCHASE, label: '구매' },
                  { value: ENROLLMENT_KIND.GRANT, label: '부여' },
                ]}
                label="종류"
                id="filterKindSelect"
              />
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={(value) => handleDateChange('startDate', value)}
                onEndDateChange={(value) => handleDateChange('endDate', value)}
                startDateId="startDateInput"
                endDateId="endDateInput"
                endDateAriaLabel="종료 날짜"
              />
            </div>
            {isLoadingEnrollments ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : enrollmentsData && enrollmentsData.data.length > 0 ? (
              <div>
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                        수강 ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                        계정 ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                        상태
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                        종류
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                        구매날짜
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                        수강 시작일
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {enrollmentsData.data.map((enrollment) => (
                      <tr
                        key={enrollment.id}
                        onClick={() => navigate(`/dashboard/user/${enrollment.accountId}/enrollment/${enrollment.id}`)}
                        className="cursor-pointer transition-colors hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-900">{enrollment.id}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              await navigate(`/dashboard/users/${enrollment.accountId}`);
                            }}
                            className="text-indigo-600 hover:text-indigo-800 hover:underline">
                            {enrollment.accountId}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="neutral">{enrollment.state}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant={enrollment.kind === ENROLLMENT_KIND.PURCHASE ? 'primary' : 'success'}>
                            {enrollment.kind === ENROLLMENT_KIND.PURCHASE ? '구매' : '부여'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {new Date(enrollment.createdAt).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString('ko-KR') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* 페이지네이션 */}
                {enrollmentsData.meta.totalPages > 1 && (
                  <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-600">
                        {enrollmentsData.meta.total}개 중 {(enrollmentsPage - 1) * enrollmentsLimit + 1}-
                        {Math.min(enrollmentsPage * enrollmentsLimit, enrollmentsData.meta.total)}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEnrollmentsPage((p) => Math.max(1, p - 1))}
                          disabled={enrollmentsPage === 1}>
                          이전
                        </Button>
                        <div className="flex items-center gap-2">
                          {Array.from({ length: enrollmentsData.meta.totalPages }, (_, i) => i + 1)
                            .filter(
                              (p) =>
                                p === 1 || p === enrollmentsData.meta.totalPages || Math.abs(p - enrollmentsPage) <= 2,
                            )
                            .map((page, idx, arr) => (
                              <React.Fragment key={page}>
                                {idx > 0 && arr[idx - 1] !== page - 1 && <span className="text-slate-400">...</span>}
                                <Button
                                  variant={page === enrollmentsPage ? 'primary' : 'outline'}
                                  size="sm"
                                  onClick={() => setEnrollmentsPage(page)}>
                                  {page}
                                </Button>
                              </React.Fragment>
                            ))}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEnrollmentsPage((p) => Math.min(enrollmentsData.meta.totalPages, p + 1))}
                          disabled={enrollmentsPage === enrollmentsData.meta.totalPages}>
                          다음
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-sm text-slate-400">구매자가 없습니다.</div>
            )}
          </Card>

          {/* 설명 카드 */}
          <Card title="상품 설명">
            {product.description ? (
              <div
                className="prose prose-sm max-w-none text-slate-600"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(product.description),
                }}
              />
            ) : (
              <p className="text-sm text-slate-400">등록된 설명이 없습니다.</p>
            )}
          </Card>
        </div>

        {/* Right Column: Sidebar Info */}
        <div className="space-y-6">
          {/* 썸네일 & 기본 상태 */}
          <Card>
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-700">썸네일</h3>
                  <div className="group relative aspect-video overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                    {thumbnailUrl ? (
                      <img src={thumbnailUrl} alt="상품 썸네일" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-slate-400">
                        <span className="text-sm">썸네일 없음</span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-700">이미지</h3>
                  <div className="group relative aspect-video overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                    {imageUrl ? (
                      <img src={imageUrl} alt="상품 이미지" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-slate-400">
                        <span className="text-sm">이미지 없음</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h2 className="mb-1 text-lg font-bold text-slate-900">{product.title}</h2>
                {product.subtitle && <p className="mb-1 text-sm text-slate-500">{product.subtitle}</p>}
                <p className="mb-4 font-mono text-sm text-slate-500">{product.slug}</p>

                <div className="mb-6 flex flex-wrap gap-2">
                  <Badge variant={getKindBadgeVariant(product.kind)}>{PRODUCT_KIND_LABELS[product.kind]}</Badge>
                  <Badge variant={getStateBadgeVariant(product.state)}>{PRODUCT_STATE_LABELS[product.state]}</Badge>
                </div>

                <div className="space-y-3 border-t border-slate-100 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">판매 기간</span>
                    <span className="text-right font-medium text-slate-900">
                      {formatDateTime(product.openAt)} <br /> ~ {formatDateTime(product.closeAt)}
                    </span>
                  </div>
                  {product.displayEndAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">노출 종료일</span>
                      <span className="font-medium text-slate-900">{formatDateTime(product.displayEndAt)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">총 강의 시간</span>
                    <span className="font-medium text-slate-900">{product.totalDuration || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* 메타 정보 */}
          <Card title="메타 정보">
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">생성일</span>
                <span className="text-slate-700">{formatDateTime(product.createdAt)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">수정일</span>
                <span className="text-slate-700">{formatDateTime(product.updatedAt)}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        buttons={[
          {
            label: '취소',
            variant: 'outline',
            onClick: () => setConfirmModal({ ...confirmModal, isOpen: false }),
          },
          {
            label: '삭제',
            variant: 'danger',
            onClick: confirmModal.onConfirm,
          },
        ]}
      />

      {/* 상품 아이템 추가 모달 */}
      {product && (
        <ProductItemAddModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddProductItem}
          productId={productId}
          productKind={product.kind}
          existingItemCount={product.items.length}
          existingCourseIds={product.items.map((item) => item.courseId).filter((id): id is number => id !== null)}
          existingItems={product.items}
        />
      )}

      {/* 상품 아이템 수정 모달 */}
      {editModal.item && product && (
        <ProductItemEditModal
          isOpen={editModal.isOpen}
          onClose={() => setEditModal({ isOpen: false, item: null })}
          onSave={handleUpdateProductItem}
          item={editModal.item}
          productKind={product.kind}
          existingItems={product.items}
        />
      )}
    </DetailPageLayout>
  );
};

export default ProductDetail;
